import * as React from 'react';
import { Layout, DatePicker, Button, List, Popover, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { invoicesRestCrudThunksForBuyer } from '../../thunks/invoices_thunks';
import {
	compareStrings,
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { Link } from 'react-router-dom';
import { INVOICE_STATUS_DISPLAY_NAMES, INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import NextPreviousButton from '../next_previous_button/NextPreviousButton';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { updatePaginationInvoices } from '../../thunks/invoices_thunks_wrapper';
import InvoiceEditButton from '../invoice_edit_button/InvoiceEditButton';

const queryString = require('qs');
const style = require('./InvoicesDetailPage.less');

interface InvoicesDetailPageState {}

class InvoicesDetailPage extends React.Component<any, InvoicesDetailPageState> {
	state = {
		loading: true,
		backLinkTo: '/buyer/invoices',
		backLinkText: 'Back to invoices',
		fromTargetCollectionName: null,
	};

	constructor(props) {
		super(props);
	}

	componentDidMount() {
		const { getInvoice, getInvoiceDelta, location, match, invoice } = this.props;
		const invoiceId = match.params.id;

		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		if (queryParams.targetCollectionName) {
			this.setState({ fromTargetCollectionName: queryParams.targetCollectionName });
		}
		if (queryParams.backlinkParams) {
			const { pathName, backLinkText, ...otherParams } = queryString.parse(
				queryParams.backlinkParams
			);
			const finalPath = pathName || `/buyer/invoices/overview/all`;
			this.setState({
				loading: false,
				backLinkTo: `${finalPath}?${new URLSearchParams(otherParams).toString()}`,
				...(backLinkText && { backLinkText }),
			});
		} else {
			this.setState({
				loading: false,
				backLinkTo: `/buyer/invoices/overview/all`,
			});
		}
		if (invoice && invoice.id) {
			getInvoiceDelta(invoice.id);
		} else {
			getInvoice(invoiceId);
		}
	}
	componentDidUpdate(prevProps) {
		const { invoice, getInvoiceDelta } = this.props;
		const { invoice: prevInvoice } = prevProps;
		if (invoice && prevInvoice && invoice.id !== prevInvoice.id) {
			getInvoiceDelta(invoice.id);
		}
	}

	handleTabChange = (key) => {
		const { history, match } = this.props;
		const invoiceId = match.params.id;
		history.push(`/buyer/invoices/detail/${invoiceId}/${key}`);
	};

	handleDelete = () => {
		const { history, deleteInvoice, invoice } = this.props;
		deleteInvoice(invoice).then(() => {
			history.push('/buyer/invoices/overview/all');
		});
	};

	checkTabActive = (key) => {
		const { history } = this.props;
		const pathname = history.location.pathname;
		return pathname.indexOf(key) !== -1;
	};

	editInvoice = () => {
		const { match, history } = this.props;
		const invoiceId = match.params.id;
		history.push(`/buyer/invoices/edit/${invoiceId}`);
	};

	viewWorkOrder = () => {
		const { invoice, history } = this.props;
		history.push(`/buyer/workOrders/detail/${invoice.workOrderId}/details`);
	};

	render() {
		const {
			invoice,
			match,
			companyConfig,
			currentUser,
			invoices,
			location,
			history,
			updatePagination,
			cloneTargetCollection,
		} = this.props;
		const invoiceId = match.params.id;
		const tabs = [
			{
				key: 'details',
				name: 'Overview',
			},
			...(invoice && invoice.revisionChanges
				? [
						{
							key: 'revisions',
							name: 'Revisions',
						},
				  ]
				: []),
		];
		const currency = getCurrency({
			workOrder: nullSafeGet('workOrder', invoice),
			supplierFacility: nullSafeGet('supplierFacility', invoice),
		});
		// A project invoice has no work order, so the WO links would point at
		// /workOrders/detail/undefined. It links to its project instead.
		const workOrderId = nullSafeGet('workOrderId', invoice);
		const projectId = nullSafeGet('projectId', invoice);
		const locationName = nullSafeGetOrElse(
			'workOrder.location.name',
			invoice,
			nullSafeGet('location.name', invoice)
		);
		const invoiceActions = [
			...(workOrderId
				? [
						<Link target={'_'} to={`/buyer/workOrders/detail/${workOrderId}/details`}>
							<Button ghost={true}>
								View Work Order <i className="icons8-font icons8-external-link ml-2" />
							</Button>
						</Link>,
						<Link
							className="ml-3"
							target={'_'}
							to={`/buyer/workOrders/detail/${workOrderId}/notes`}
						>
							<Button ghost={true}>
								View Notes <i className="icons8-font icons8-external-link ml-2" />
							</Button>
						</Link>,
				  ]
				: []),
			...(!workOrderId && projectId
				? [
						<Link target={'_'} to={`/buyer/projects/detail/${projectId}/details`}>
							<Button ghost={true}>
								View Project <i className="icons8-font icons8-external-link ml-2" />
							</Button>
						</Link>,
				  ]
				: []),
			<InvoiceEditButton invoice={invoice} ghost={true} />,
		];
		return (
			<div className="invoicesDetailPage">
				<DetailPageHeader
					exactPath={'/buyer/invoices/detail/:id'}
					redirectPath={`/buyer/invoices/detail/${invoiceId}/details`}
					backLinkText={this.state.backLinkText}
					backLinkTo={this.state.backLinkTo}
					// A project invoice carries its own title precisely so several under one
					// project can be told apart; the project's title is the fallback for the ones
					// created before that field existed. Same order as InvoiceRowDisplay.
					title={nullSafeGetOrElse(
						'workOrder.title',
						invoice,
						nullSafeGetOrElse('title', invoice, nullSafeGet('project.title', invoice))
					)}
					subtitle={
						<>
							<div className="invoicesDetailPage__subtitleRow">
								<div className="invoicesDetailPage__subtitle">
									{INVOICE_STATUS_DISPLAY_NAMES[invoice.status]}
								</div>
								<div className="invoicesDetailPage__subtitle">
									{currency.format(invoice.invoiceTotalAfterTax)}
								</div>
							</div>
							{locationName ? (
								<div className="body1" style={{ marginTop: 8 }}>
									{locationName}
								</div>
							) : null}
						</>
					}
					actions={
						<div className="flex flex-row flex-wrap justify-end gap-y-4">{invoiceActions}</div>
					}
					tabs={tabs}
					backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
					handleTabChange={this.handleTabChange}
					checkTabActive={this.checkTabActive}
					childRoutes={this.props.childRoutes}
					tabsRightActions={() => (
						<NextPreviousButton
							key={this.state.fromTargetCollectionName}
							location={location}
							history={history}
							activeId={invoice.id}
							getEnitityUrl={(id) => `/buyer/invoices/detail/${id}/details`}
							stateSlice={invoices}
							targetCollectionName={this.state.fromTargetCollectionName}
							updatePagination={updatePagination}
							cloneTargetCollection={cloneTargetCollection}
							entityName="invoice"
						/>
					)}
				/>
			</div>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	invoice: getEntityById(ownProps.match.params.id, state.invoices),
	invoices: state.invoices,
	match: ownProps.match,
	history: ownProps.history,
	companyConfig: state.company_config,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getInvoice: (id) => dispatch(invoicesRestCrudThunksForBuyer.readOne(id)),
	getInvoiceDelta: (id) => dispatch(invoicesRestCrudThunksForBuyer.readDelta(id)),
	deleteInvoice: (entity) =>
		dispatch(invoicesRestCrudThunksForBuyer.delete(entity, 'id', null, true)),
	cloneTargetCollection: (from, to) =>
		dispatch(INVOICES_CRUD_ACTION_CREATORS.cloneTargetCollection(from, to)),
	updatePagination: (pagination, targetCollectionName) =>
		dispatch(
			updatePaginationInvoices('buyer')(
				false,
				ownProps.history,
				targetCollectionName,
				pagination,
				true
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(InvoicesDetailPage)
);
